import React from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const steps = [
  {
    num: "01",
    title: "Understanding Business Challenges",
    desc: "We identify where your business is struggling — outdated systems, weak online presence, low engagement, or lack of automation.",
    icon: "📉",
    iconBg: "bg-blue-100",
    side: "left",
  },
  {
    num: "02",
    title: "Planning the Right Digital Solution",
    desc: "Whether it's a website, app, software, AI automation, or content strategy, we create a solution tailored to your business goals.",
    icon: "🧠",
    iconBg: "bg-indigo-100",
    side: "right",
  },
  {
    num: "03",
    title: "Designing & Building the Product",
    desc: "Our team develops modern websites, mobile apps, custom software, AI solutions, and high-quality creative content with precision.",
    icon: "⚙️",
    iconBg: "bg-green-100",
    side: "left",
  },
  {
    num: "04",
    title: "Creating Content That Converts",
    desc: "We produce UGC videos, professional video editing, motion graphics, and marketing content that increases trust and customer engagement.",
    icon: "🎬",
    iconBg: "bg-yellow-100",
    side: "right",
  },
  {
    num: "05",
    title: "Launch, Scale & Long-Term Support",
    desc: "After delivery, we continue supporting your growth with updates, optimizations, scaling strategies, and ongoing technical support.",
    icon: "🚀",
    iconBg: "bg-teal-100",
    side: "left",
  },
];

function StepCard({ s }) {
  return (
    <div className="group relative w-[46%] bg-white rounded p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:shadow-blue-100 hover:-translate-y-1 transition-all duration-300 overflow-hidden">
      <div className={`w-12 h-12 rounded ${s.iconBg} flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform duration-300`}>
        {s.icon}
      </div>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-4 h-px bg-gradient-to-r from-blue-400 to-sky-400" />
        <span className="text-[10px] font-bold text-blue-600 tracking-widest uppercase">Step {s.num}</span>
      </div>
      <h3 className="font-display font-bold text-base text-black mb-1.5">{s.title}</h3>
      <p className="text-slate-500 text-xs leading-relaxed font-medium">{s.desc}</p>
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-sky-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded" />
    </div>
  );
}

export default function Process() {
  const ref = useScrollAnimation();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Fraunces:wght@700;900&display=swap');
        .prc-heading { font-family: 'Fraunces', serif; }
        .prc-body { font-family: 'Plus Jakarta Sans', sans-serif; }
      `}</style>

      <section id="process" className="prc-body py-20 relative bg-gradient-to-b from-white to-blue-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-sky-100/40 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto px-5 md:px-10 relative">
          {/* Heading */}
          <div ref={ref} className="section-animate text-center mb-14">
            <span className="inline-block text-xs font-bold text-blue-600 tracking-widest uppercase bg-blue-100 border border-blue-200 rounded px-5 py-1.5 mb-5">
              How We Work
            </span>
            <h2 className="prc-heading font-black text-4xl md:text-6xl text-black mb-4 leading-tight">
              Our{" "}
              <span style={{ background: "linear-gradient(135deg,#1d4ed8 0%,#0ea5e9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Process
              </span>
            </h2>
            <p className="text-slate-500 text-lg max-w-xl mx-auto font-medium">
              Transparent, structured, and built for clarity at every step.
            </p>
          </div>

          {/* Zigzag Steps */}
          <div className="relative">
            {steps.map((s, i) => {
              const isLeft = s.side === "left";
              const isFirst = i === 0;
              const isLast = i === steps.length - 1;

              return (
                <div key={i} className="flex items-stretch">
                  {/* Left side */}
                  {isLeft ? <StepCard s={s} /> : <div className="w-[46%]" />}

                  {/* Center spine */}
                  <div className="w-[8%] flex flex-col items-center">
                    {!isFirst && <div className="w-0.5 flex-1 bg-gradient-to-b from-blue-400 to-sky-300 opacity-30" />}
                    {isFirst && <div className="flex-1" />}
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-sky-400 flex items-center justify-center text-white text-xs font-black shadow-lg shadow-blue-200 ring-[6px] ring-blue-100 z-10 flex-shrink-0">
                      {s.num}
                    </div>
                    {!isLast && <div className="w-0.5 flex-1 bg-gradient-to-b from-sky-300 to-blue-400 opacity-30" />}
                    {isLast && <div className="flex-1" />}
                  </div>

                  {/* Right side */}
                  {!isLeft ? <StepCard s={s} /> : <div className="w-[46%]" />}
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="text-center mt-14">
            <p className="text-slate-400 text-sm mb-4 font-medium">Ready to get started? Let's build together.</p>
            <a href="#contact" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-8 py-4 rounded transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
              Start Your Project →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}